import { useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { adminApi } from '@/lib/api';
import i18n from '@/i18n';

const ADMIN_API_URL = adminApi();

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface CompanyUser {
  id: string;
  email: string;
  role: 'company_admin' | 'user';
  is_active: boolean;
  has_active_session: boolean;
  created_at: string;
}

export interface CompanyDashboardStats {
  company_name: string;
  users_count: number;
  today_usage: number;
  month_usage: number;
  week_usage: number;
  recent_activity: Array<{
    id: string;
    usage_type: string;
    created_at: string;
    user_email: string;
    metadata: Record<string, unknown>;
  }>;
}

export interface CompanyUsageStats {
  totals: {
    linkedin: number;
    messages: number;
    audio: number;
    total: number;
  };
  by_user: Array<{
    user_id: string;
    email: string;
    linkedin: number;
    messages: number;
    audio: number;
    total: number;
  }>;
  chart_data: Array<{
    date: string;
    linkedin: number;
    messages: number;
    audio: number;
  }>;
  truncated?: boolean;
}

export function useCompanyApi() {
  const { getToken } = useAuthContext();

  const callApi = useCallback(async <T>(
    action: string,
    params: Record<string, unknown> = {}
  ): Promise<ApiResponse<T>> => {
    const token = getToken();
    
    if (!token) {
      return { success: false, error: i18n.t('noPermission', { ns: 'errors' }) };
    }

    try {
      const response = await fetch(ADMIN_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ action, token, ...params }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Company API error:', error);
      return { success: false, error: i18n.t('auth.connectionError', { ns: 'errors' }) };
    }
  }, [getToken]);

  // Dashboard Stats
  const getDashboardStats = useCallback(() => 
    callApi<CompanyDashboardStats>('get_company_dashboard_stats'), [callApi]);

  // Company Users
  const listUsers = useCallback(() =>
    callApi<CompanyUser[]>('list_company_users'), [callApi]);

  // Usage Stats
  const getUsageStats = useCallback((params: { start_date?: string; end_date?: string } = {}) =>
    callApi<CompanyUsageStats>('get_company_usage_stats', params), [callApi]);

  return {
    getDashboardStats,
    listUsers,
    getUsageStats,
  };
}
